import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Inbox, ArrowUpRight, Loader2 } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { trackProductClick } from "@/utils/tracking";
import { setPageMeta } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = (process.env.REACT_APP_LEAD_MAGNET_PDF_URL || "").trim();

export default function ThankYou() {
  const [product, setProduct] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Terima Kasih | NALURA",
      "Panduan Good Mind for Money sedang dikirim ke emailmu.",
      {
        robots: "noindex",
        og: { image: `${window.location.origin}/cover-good-mind-for-money.jpg` },
      }
    );
    axios.get(`${API}/products`).then((r) => {
      const list = r.data.filter((p) => !p.coming_soon);
      setProduct(list.find((p) => p.featured) || list[0] || null);
    }).catch(() => {});
  }, []);

  const handleBuy = async () => {
    if (!product) return;
    setBuying(true);
    trackProductClick(product.name, "terima-kasih");
    try {
      const res = await axios.post(`${API}/products/${product.product_id}/click`, { source: "lead-magnet" });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(product.affiliate_url, "_blank", "noopener,noreferrer");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen" data-testid="thank-you-page">
      <Nav />
      <main className="pt-28 md:pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Terima Kasih Sudah Bergabung</p>
        <h1 className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
          Panduanmu sedang dalam perjalanan.
        </h1>
        <p className="text-base text-[#211B10]/65 leading-relaxed mb-10 max-w-xl mx-auto">
          Kami sudah mengirim <em>"Good Mind for Money"</em> ke emailmu. Biasanya tiba dalam beberapa menit.
          Kalau belum terlihat, cek tab Promosi atau Spam, lalu pindahkan email dari NALURA ke Kotak Masuk Utama supaya
          catatan berikutnya tidak terlewat. Tidak mau menunggu? Unduh langsung lewat tombol di bawah, lalu mulai dari
          Kebiasaan 01 malam ini atau ritual pagi besok saat bangun.
        </p>

        {PDF_URL && (
          <a
            data-testid="download-pdf-button"
            href={PDF_URL}
            download
            className="inline-flex items-center gap-3 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#211B10] hover:-translate-y-1 transition-[background-color,transform] duration-300"
          >
            <Download className="w-4 h-4" /> Unduh Panduan Gratis
          </a>
        )}

        <div className="mt-8 flex items-center justify-center gap-3 text-xs text-[#211B10]/55">
          <Inbox className="w-4 h-4 text-[#A67C1E]" />
          <span>Simpan PDF ini baik-baik, dan pindahkan email NALURA ke Kotak Masuk Utama agar tidak terlewat.</span>
        </div>

        {product && (
          <div data-testid="thankyou-product-card" className="mt-16 border border-[#211B10]/10 bg-white p-8 md:p-10 text-left">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A67C1E] mb-4">Langkah Berikutnya</p>
            <div className="grid sm:grid-cols-[180px_1fr] gap-6 items-center">
              <img src={product.image_url} alt={product.name} loading="lazy" className="w-full aspect-[4/3] object-cover border border-[#211B10]/10" />
              <div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-2">{product.name}</h2>
                <p className="text-sm text-[#211B10]/60 leading-relaxed mb-5">{product.tagline}</p>
                <button
                  data-testid="thankyou-buy-button"
                  onClick={handleBuy}
                  disabled={buying}
                  className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-xs uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-[#211B10] transition-colors duration-300 disabled:opacity-60"
                >
                  {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  Amankan Akses Saya
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
